import apiClient from './apiClient';

export interface CategoryData {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  iconUrl?: string;
  parentCategory?: string | null;
  ancestors?: string[];
  isActive: boolean;
  dealCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CategoryTreeNode extends CategoryData {
  children: CategoryTreeNode[];
  dealCount?: number;
  avgDiscount?: number;
  totalDeals?: number;
}

export interface CategoryWithRelations extends CategoryData {
  children?: CategoryData[];
  parent?: CategoryData | null;
}

export interface CategoryStats {
  totalCategories: number;
  totalSubcategories: number;
  totalDeals: number;
  totalBrands: number;
  averageSavings: number | null; // Can be null if no discount data
}

const API_PATH = '/categories';

// Get all categories
export const getCategories = async (
  options: {
    includeCounts?: boolean;
    activeOnly?: boolean;
    parentOnly?: boolean;
  } = {}
): Promise<CategoryData[]> => {
  try {
    const queryParams = new URLSearchParams();

    if (options.includeCounts) {
      queryParams.append('includeCounts', 'true');
    }
    if (options.activeOnly === false) {
      queryParams.append('activeOnly', 'false');
    }
    if (options.parentOnly) {
      queryParams.append('parentOnly', 'true');
    }

    const url = queryParams.toString() ? `${API_PATH}?${queryParams}` : API_PATH;
    const response = await apiClient.get(url);
    return response.data.data || [];
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
};

// Get all category page data in one request (RECOMMENDED for Categories page)
export const getCategoryPageData = async (): Promise<{
  stats: CategoryStats;
  tree: CategoryTreeNode[];
}> => {
  try {
    const response = await apiClient.get(`${API_PATH}/page-data`);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching category page data:', error);
    // Re-throw error - let the component handle the error state
    // NEVER return fake/fallback data
    throw error;
  }
};

// Get category tree (hierarchical structure)
// @deprecated Use getCategoryPageData() for Categories page - kept for SubcategoryPage
export const getCategoryTree = async (): Promise<CategoryTreeNode[]> => {
  try {
    const response = await apiClient.get(`${API_PATH}/tree`);
    return response.data.data || [];
  } catch (error) {
    console.error('Error fetching category tree:', error);
    throw error;
  }
};

// Get category statistics for hero section
// @deprecated Use getCategoryPageData() for Categories page
export const getCategoryStats = async (): Promise<CategoryStats> => {
  try {
    const response = await apiClient.get(`${API_PATH}/stats`);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching category stats:', error);
    // Re-throw error - NEVER return fake data
    throw error;
  }
};

// Get category by slug with children and parent
export const getCategoryBySlug = async (slug: string): Promise<CategoryWithRelations | null> => {
  try {
    const response = await apiClient.get(`${API_PATH}/slug/${encodeURIComponent(slug)}`);
    return response.data.data || null;
  } catch (error) {
    console.error(`Error fetching category with slug ${slug}:`, error);
    return null;
  }
};

// Get only top-level categories
export const getTopLevelCategories = async (): Promise<CategoryData[]> => {
  return getCategories({ parentOnly: true, activeOnly: true });
};

// Get children of a category
export const getCategoryChildren = async (parentSlug: string): Promise<CategoryData[]> => {
  try {
    const parent = await getCategoryBySlug(parentSlug);
    return parent?.children || [];
  } catch (error) {
    console.error(`Error fetching children for ${parentSlug}:`, error);
    return [];
  }
};

// Build breadcrumb path for a category
export const getCategoryBreadcrumbs = async (slug: string): Promise<CategoryData[]> => {
  try {
    const category = await getCategoryBySlug(slug);
    if (!category) return [];

    const breadcrumbs: CategoryData[] = [];
    let current: CategoryWithRelations | null = category;

    // Add current category
    breadcrumbs.unshift(current as CategoryData);

    // Traverse up the hierarchy
    while (current?.parent) {
      breadcrumbs.unshift(current.parent);
      // Fetch parent's parent if needed
      if (current.parent.slug) {
        current = await getCategoryBySlug(current.parent.slug);
      } else {
        break;
      }
    }

    return breadcrumbs;
  } catch (error) {
    console.error(`Error building breadcrumbs for ${slug}:`, error);
    return [];
  }
};

// Get category by hierarchical path (e.g., ['electronics', 'gaming', 'consoles'])
export const getCategoryByPath = async (
  pathSegments: string[]
): Promise<{
  category: CategoryData;
  breadcrumbs: CategoryData[];
  subcategories: CategoryData[]; // NEW: Include subcategories
} | null> => {
  try {
    const path = pathSegments.join('/');
    const response = await apiClient.get(`${API_PATH}/path/${encodeURIComponent(path)}`);

    if (response.data.success && response.data.data) {
      return {
        category: response.data.data.category,
        breadcrumbs: response.data.data.breadcrumbs || [],
        subcategories: response.data.data.subcategories || [], // NEW: Extract subcategories from response
      };
    }
    return null;
  } catch (error) {
    console.error(`Error fetching category by path ${pathSegments.join('/')}:`, error);
    return null;
  }
};
